import React, { useState } from 'react'
import { Modal, Form } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import './AddContactStyle.css'
import { IContact } from '@dataTypes/contact.type'
import { InputField } from '@components/common'
import AddressFields from './AddressFields.component'
import { titleOptions } from '@constants/Common/personalInfo.constants'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useGlobalLoadingActions } from '@hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { addContact, fetchAllCallLogs, updateContact } from '@store/actions'
import { preferredCallMethodOptions } from '@constants/Message'
import { setContactPhoneNumber } from '@store/reducers/contact.reducer'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'

const defaultContactData: IContact = {
  clinic_id: '',
  title: 'Mr.',
  first_name: '',
  last_name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  cellphone: '',
}

type AddContactModalProps = {
  show: boolean
  onHide: () => void
  initialContactData?: IContact
  fetchCalls?: boolean
}

const ADD_CONTACT_MODAL_LOADING_CONTAINER = '.modal.show .modal-content'

const AddContactModal: React.FC<AddContactModalProps> = ({
  show,
  onHide,
  initialContactData,
  fetchCalls = false,
}) => {
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const phoneNumber = useAppSelector((state) => state.contacts.phoneNumber)
  const contacts = useAppSelector((state) => state.contacts.data)
  const [contact, setContact] = useState<IContact>(() => {
    if (initialContactData) {
      return {
        ...defaultContactData,
        ...initialContactData,
        cellphone:
          initialContactData.cellphone ||
          (phoneNumber && !initialContactData.fax_number ? phoneNumber : ''),
      }
    }
    if (phoneNumber) {
      return { ...defaultContactData, cellphone: phoneNumber }
    }
    return defaultContactData
  })
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof IContact, string>>
  >({})

  const handleHide = (): void => {
    dispatch(setContactPhoneNumber(null))
    onHide()
  }

  const validateFields = (
    fields: Partial<IContact>,
  ): { [key: string]: string } => {
    const requiredFields: (keyof IContact)[] = [
      'title',
      'first_name',
      'last_name',
      'address',
      'city',
      'state',
      'zip',
      'cellphone',
      'email',
    ]
    const errors: { [key: string]: string } = {}
    requiredFields.forEach((field) => {
      const value = fields[field]
      const formattedField = field
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
      if (!value || value === '') {
        errors[field] = `${formattedField} is required`
      }
    })

    // Check email format
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (fields.email && !emailRegex.test(fields.email)) {
      errors.email = 'Invalid email address'
    }

    // Check phone format
    const validatePhoneNumber = (phone: string | undefined): boolean => {
      if (!phone) return true // Skip validation if phone is empty

      const cleaned = phone.replace(/[()\s-]/g, '')

      return (
        /^\+?\d{10,11}$/.test(cleaned) &&
        (cleaned.startsWith('+')
          ? cleaned.length === 12
          : cleaned.length === 10 || cleaned.length === 11)
      )
    }

    // Validate cellphone (required)
    if (!validatePhoneNumber(fields.cellphone)) {
      errors.cellphone = 'Invalid cellphone number'
    }

    // Validate optional phone numbers
    if (fields.home_phone && !validatePhoneNumber(fields.home_phone)) {
      errors.home_phone = 'Invalid home phone number'
    }

    if (fields.work_phone && !validatePhoneNumber(fields.work_phone)) {
      errors.work_phone = 'Invalid work phone number'
    }

    if (fields.fax_number && !validatePhoneNumber(fields.fax_number)) {
      errors.fax_number = 'Invalid fax number'
    }

    // Check for duplicate phone numbers across existing contacts
    const checkDuplicate = (phone: string | undefined): boolean => {
      if (!phone) return false
      const normalized = normalizePhoneNumber(phone)
      if (!normalized) return false
      return contacts.some((c) => {
        // Allow the same number on the contact being edited
        if (
          initialContactData?.contact_id &&
          c.contact_id === initialContactData.contact_id
        ) {
          return false
        }
        const numbersToCheck = [
          c.cellphone,
          c.home_phone,
          c.work_phone,
          c.fax_number,
        ].filter(Boolean) as string[]
        return numbersToCheck.some(
          (n) => normalizePhoneNumber(n) === normalized,
        )
      })
    }

    if (fields.cellphone && checkDuplicate(fields.cellphone)) {
      errors.cellphone = 'This phone number is already used'
    }

    if (fields.home_phone && checkDuplicate(fields.home_phone)) {
      errors.home_phone = 'This phone number is already used'
    }

    if (fields.work_phone && checkDuplicate(fields.work_phone)) {
      errors.work_phone = 'This phone number is already used'
    }

    if (fields.fax_number && checkDuplicate(fields.fax_number)) {
      errors.fax_number = 'This fax number is already used'
    }

    return errors
  }

  const handleInputChange = (
    field: keyof IContact,
    value: string | undefined,
  ): void => {
    setContact({ ...contact, [field]: value })
    if (Object.keys(validationErrors).length > 0) {
      const updatedContactData: IContact = {
        ...contact,
        first_name: contact.first_name.trim(),
        last_name: contact.last_name.trim(),
        email: contact.email.trim(),
        cellphone: contact.cellphone.trim(),
        home_phone: contact.home_phone?.trim(),
        work_phone: contact.work_phone?.trim(),
      }

      const newErrors = validateFields(updatedContactData)
      setValidationErrors(newErrors)
    }
  }

  const handleSave = async (): Promise<void> => {
    const updatedContactData: IContact = {
      ...contact,
      first_name: contact.first_name.trim(),
      last_name: contact.last_name.trim(),
      email: contact.email.trim(),
      cellphone: contact.cellphone.trim(),
      home_phone: contact.home_phone?.trim(),
      work_phone: contact.work_phone?.trim(),
      fax_number: contact.fax_number?.trim(),
    }

    const newErrors = validateFields(updatedContactData)
    setValidationErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    const updates = {
      ...updatedContactData,
      cellphone: updatedContactData.cellphone.replace(/[()\s-]/g, ''),
      home_phone: updatedContactData.home_phone?.replace(/[()\s-]/g, ''),
      work_phone: updatedContactData.work_phone?.replace(/[()\s-]/g, ''),
      fax_number: updatedContactData.fax_number?.replace(/[()\s-]/g, ''),
    }

    try {
      showLoading({ container: ADD_CONTACT_MODAL_LOADING_CONTAINER })
      if (initialContactData?.contact_id) {
        await dispatch(
          updateContact({
            contact_id: initialContactData.contact_id,
            updates,
          }),
        ).unwrap()
        if (fetchCalls) dispatch(fetchAllCallLogs())
        showToast(
          createSuccessToast(
            'Contact updated',
            'Contact has been updated successfully.',
          ),
        )
      } else {
        await dispatch(addContact(updatedContactData)).unwrap()
        if (fetchCalls) dispatch(fetchAllCallLogs())
        showToast(
          createSuccessToast(
            'Contact saved',
            'Contact has been saved successfully.',
          ),
        )
      }
      handleHide()
    } catch {
      showToast(
        createFailedToast(
          initialContactData?.contact_id ? 'Update failed' : 'Save failed',
          initialContactData?.contact_id
            ? 'Updating contact failed. Please try again.'
            : 'Saving contact failed. Please try again.',
        ),
      )
    } finally {
      hideLoading(ADD_CONTACT_MODAL_LOADING_CONTAINER)
    }
  }

  const hasNoChanges = (
    initial: IContact | undefined,
    current: IContact,
  ): boolean => {
    if (!initial) return false

    const fieldsToCompare: (keyof IContact)[] = [
      'title',
      'first_name',
      'middle_name',
      'preferred_name',
      'last_name',
      'address',
      'city',
      'state',
      'zip',
      'cellphone',
      'email',
      'home_phone',
      'work_phone',
      'preferred_call_method',
    ]

    return fieldsToCompare.every((field) => {
      const initialValue = (initial[field] as string)?.trim() ?? ''
      const currentValue = (current[field] as string)?.trim() ?? ''
      return initialValue === currentValue
    })
  }

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size="lg"
      className="add-contact-modal"
      backdrop="static"
    >
      <Modal.Header className="bg-custom3 mb-3" closeButton>
        <Modal.Title>
          {initialContactData ? 'Edit ' : 'Add '}contact
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="acm-row acm-row-1">
          <div className="title-slot">
            <div className="acm-input-label">Title</div>
            <Form.Select
              value={contact.title || ''}
              onChange={(e) => handleInputChange('title', e.target.value)}
            >
              {titleOptions.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="fname-slot">
            <InputField
              label="First name"
              placeholder="ex. John"
              value={contact.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              hint={validationErrors.first_name || undefined}
              destructive={!!validationErrors.first_name}
            />
          </div>

          <div className="lname-slot">
            <InputField
              label="Last name"
              placeholder="ex. Doe"
              value={contact.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              hint={validationErrors.last_name || undefined}
              destructive={!!validationErrors.last_name}
            />
          </div>
        </div>

        <div className="acm-row acm-row-2">
          <div className="mname-slot">
            <InputField
              label={
                <>
                  <span className="acm-input-label">Middle name</span>
                  <span className="acm-opt">(Optional)</span>
                </>
              }
              placeholder="ex. Knox"
              value={contact.middle_name}
              onChange={(e) => handleInputChange('middle_name', e.target.value)}
              hint={validationErrors.middle_name || undefined}
              destructive={!!validationErrors.middle_name}
            />
          </div>

          <div className="pname-slot">
            <InputField
              label={
                <>
                  <span className="acm-input-label">Preferred name </span>
                  <span className="acm-opt">(Optional)</span>
                </>
              }
              placeholder="Preferred name or N/A"
              value={contact.preferred_name}
              onChange={(e) =>
                handleInputChange('preferred_name', e.target.value)
              }
            />
          </div>
        </div>

        <AddressFields
          contact={contact}
          handleInputChange={handleInputChange}
          validationErrors={validationErrors}
        />

        <div className="acm-row acm-row-5">
          <div className="cphone-slot">
            <InputField
              label="Cell phone"
              placeholder="(201) 555-0179"
              type="tel"
              value={contact.cellphone}
              onChange={(e) => handleInputChange('cellphone', e.target.value)}
              hint={validationErrors.cellphone || undefined}
              destructive={!!validationErrors.cellphone}
            />
          </div>
          <div className="email-slot">
            <InputField
              label="Email"
              placeholder="example@email.com"
              type="email"
              value={contact.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              hint={validationErrors.email || undefined}
              destructive={!!validationErrors.email}
            />
          </div>
        </div>

        <div className="acm-row acm-row-6">
          <div className="hnumber-slot">
            <InputField
              label={
                <>
                  <span className="acm-input-label">Home number</span>
                  <span className="acm-opt">(Optional)</span>
                </>
              }
              placeholder="(201) 555-0133"
              type="tel"
              value={contact.home_phone}
              onChange={(e) => handleInputChange('home_phone', e.target.value)}
              hint={validationErrors.home_phone || undefined}
              destructive={!!validationErrors.home_phone}
            />
          </div>
          <div className="wnumber-slot">
            <InputField
              label={
                <>
                  <span className="acm-input-label">Work number</span>
                  <span className="acm-opt">(Optional)</span>
                </>
              }
              placeholder="(201) 555-0143"
              type="tel"
              value={contact.work_phone}
              onChange={(e) => handleInputChange('work_phone', e.target.value)}
              hint={validationErrors.work_phone || undefined}
              destructive={!!validationErrors.work_phone}
            />
          </div>
          <div className="wnumber-slot">
            <InputField
              label={
                <>
                  <span className="acm-input-label">Fax number</span>
                  <span className="acm-opt">(Optional)</span>
                </>
              }
              placeholder="(201) 555-0199"
              type="tel"
              value={contact.fax_number ?? ''}
              onChange={(e) => handleInputChange('fax_number', e.target.value)}
              hint={validationErrors.fax_number || undefined}
              destructive={!!validationErrors.fax_number}
            />
          </div>
        </div>
        <div className="call-method-slot">
          <div className="acm-input-label">Preferred call method</div>
          <Form.Select
            value={contact.preferred_call_method ?? ''}
            onChange={(e) =>
              handleInputChange('preferred_call_method', e.target.value)
            }
            disabled={preferredCallMethodOptions.length === 0}
          >
            {preferredCallMethodOptions.map((method) => (
              <option
                key={method.id}
                value={method.id}
                disabled={!contact[method.id as keyof IContact]}
              >
                {method.text}
              </option>
            ))}
          </Form.Select>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="ms-auto">
          <>
            <Button
              size="md"
              className="button-secondary me-2"
              onClick={handleHide}
            >
              Cancel
            </Button>
            <Button
              size="md"
              className="button-primary"
              onClick={handleSave}
              disabled={
                initialContactData && hasNoChanges(initialContactData, contact)
              }
            >
              Save
            </Button>
          </>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default AddContactModal
